import { z } from 'zod';
import { assetLotTypeSchema } from './listing.js';

/**
 * asset_lot DTOs (Epic 1, "Lots as a First-Class Entity" —
 * plans/lots-first-class.md). Previously lot schemas were inline Zod in
 * apps/api/src/routes/client/lots.ts and the UI redeclared local interfaces
 * (apps/customer/src/routes/assets/index.tsx-style `interface AssetRow`) —
 * this is the first shared, single-source-of-truth module for them.
 *
 * team_id is DELIBERATELY excluded from every write schema below:
 *   - createLotSchema: team assignment at creation is member-forced
 *     server-side (a member's own team, via the tg_asset_lot_assign_creator_team
 *     trigger — migration 735) or falls back to the org's Unassigned team
 *     for an admin creator (migration 738 — asset_lot.team_id is NOT NULL as
 *     of 738, so "leave it NULL" is no longer a representable creation
 *     state). It is never a client-supplied value.
 *   - patchLotSchema: team_id is excluded per the plan's explicit
 *     instruction — it is a privilege column, mutated ONLY via
 *     client_assign_lot_team (admin-gated, SECURITY DEFINER, empty-lot-only).
 */

export const createLotSchema = z.object({
  name: z.string().trim().min(1).max(160),
  lotType: assetLotTypeSchema.default('lot'),
  notes: z.string().nullable().optional(),
});
export type CreateLotInput = z.infer<typeof createLotSchema>;

export const patchLotSchema = z
  .object({
    name: z.string().trim().min(1).max(160).optional(),
    lotType: assetLotTypeSchema.optional(),
    notes: z.string().nullable().optional(),
    status: z.enum(['draft', 'listed', 'sold', 'withdrawn']).optional(),
    dataCenterId: z.string().uuid().nullable().optional(),
    internalLocation: z.string().trim().max(160).nullable().optional(),
    isCompleteCabinet: z.boolean().nullable().optional(),
  })
  .strict();
export type PatchLotInput = z.infer<typeof patchLotSchema>;

/**
 * confirm (default false, 749): if the asset already belongs to a DIFFERENT
 * lot, the first request is rejected with reason 'already_in_lot' (409) so
 * the UI can show a "move it here?" confirmation dialog. Only a retry with
 * confirm: true actually performs the reassignment (client_set_asset_lot's
 * p_confirm) — prevents silently "poaching" an asset out of another lot.
 */
export const lotMemberSchema = z.object({
  assetId: z.string().uuid(),
  confirm: z.boolean().optional(),
});
export type LotMemberInput = z.infer<typeof lotMemberSchema>;

/**
 * PUT /api/client/lots/:lotId/team body — admin-only, empty-lot-only
 * (client_assign_lot_team). teamId is a required uuid, not nullable — as of
 * migration 738, asset_lot.team_id is NOT NULL, so "no team" no longer
 * exists as a representable state (client_assign_lot_team rejects a NULL
 * p_team_id with {ok:false, reason:'team_required'}). To "unassign" a lot,
 * point it at the org's Unassigned team instead of clearing it.
 */
export const assignLotTeamSchema = z.object({ teamId: z.string().uuid() });
export type AssignLotTeamInput = z.infer<typeof assignLotTeamSchema>;

/**
 * GET /api/client/lots list-row shape. Epic 1 adds teamId / dataCenterId /
 * dataCenterName / internalLocation / isCompleteCabinet + derived totalPowerDrawWatts
 * (joined off the RLS-scoped `asset` relation — NEVER off telemetry_daily_agg
 * keyed by lot, per the plan's Epic 0 rationale: a lot-keyed telemetry read
 * would bypass the team/org gating that only the per-asset relation carries).
 */
export interface LotListRow {
  id: string;
  name: string;
  lotType: string;
  status: string;
  createdAt: string;
  teamId: string | null;
  teamName: string | null;
  dataCenterId: string | null;
  dataCenterName: string | null;
  internalLocation: string | null;
  isCompleteCabinet: boolean | null;
  memberCount: number;
  fmvMedTotalUsd: number;
  /** Sum of each member's most recent telemetry_daily_agg.avg_power_draw_watts,
   *  joined off the RLS-scoped `asset` relation (never off a lot-keyed read). */
  totalPowerDrawWatts: number | null;
  /** Members reporting power draw / total members — coverage note. */
  powerDrawReporting: number;
}

/** Derived cabinet span: the asset-class mix present, for "is this lot one class or several". */
export interface LotCabinetSpan {
  assetClassId: string;
  assetClassName: string;
  count: number;
}

/** GET /api/client/lots/:lotId detail-only additions (Epic 1). */
export interface LotDetailExtras {
  teamId: string | null;
  teamName: string | null;
  dataCenterId: string | null;
  dataCenterName: string | null;
  internalLocation: string | null;
  isCompleteCabinet: boolean | null;
  /** Sum of each member's most recent telemetry_daily_agg.avg_power_draw_watts,
   *  joined off the RLS-scoped `asset` relation (never off a lot-keyed read). */
  totalPowerDrawWatts: number | null;
  /** Members reporting power draw / total members — coverage note, same shape as FMV/health. */
  powerDrawReporting: number;
  cabinetSpan: LotCabinetSpan[];
}
